import { Injectable, computed, inject, signal } from '@angular/core';
import { defaultIfEmpty, firstValueFrom, Subject, takeUntil } from 'rxjs';

import {
  Domain,
  UserAccessProfile,
  canAccessDomain,
  isGlobalAdmin,
  normalizeUserAccessProfile,
} from './user-access.model';
import { UserAccessApiService } from './user-access-api.service';

@Injectable({ providedIn: 'root' })
export class UserAccessService {
  private readonly api = inject(UserAccessApiService);
  private readonly currentUserState = signal<UserAccessProfile | null>(null);
  private readonly loadingState = signal(false);
  private readonly loadedState = signal(false);
  private readonly errorState = signal<string | null>(null);
  private loadingPromise: Promise<void> | null = null;
  private readonly cancelInitialization = new Subject<void>();

  readonly currentUser = this.currentUserState.asReadonly();
  readonly loading = this.loadingState.asReadonly();
  readonly loaded = this.loadedState.asReadonly();
  readonly error = this.errorState.asReadonly();
  readonly isGlobalAdmin = computed(() => isGlobalAdmin(this.currentUserState()));

  canAccess(domain: Domain): boolean {
    return canAccessDomain(this.currentUserState(), domain);
  }

  initialize(): Promise<void> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingState.set(true);
    this.errorState.set(null);
    this.loadingPromise = firstValueFrom(
      this.api.getMe().pipe(takeUntil(this.cancelInitialization), defaultIfEmpty(undefined)),
    )
      .then((user) => {
        if (user) {
          this.currentUserState.set(normalizeUserAccessProfile(user));
        } else {
          this.currentUserState.set(null);
          this.errorState.set('Your access profile loading was cancelled.');
        }
      })
      .catch(() => {
        this.currentUserState.set(null);
        this.errorState.set('Your access profile could not be loaded.');
      })
      .finally(() => {
        this.loadingState.set(false);
        this.loadedState.set(true);
        this.loadingPromise = null;
      });

    return this.loadingPromise;
  }

  cancelInitializationRequest(): void {
    if (this.loadingState()) this.cancelInitialization.next();
  }

  retry(): Promise<void> {
    this.loadedState.set(false);
    return this.initialize();
  }

  refreshIfCurrent(user: UserAccessProfile): void {
    if (this.currentUserState()?.id === user.id) {
      this.currentUserState.set(normalizeUserAccessProfile(user));
      void this.retry();
    }
  }
}
